import { useEffect, useState, useCallback } from 'react'
import { lumi } from '@/lib/lumi'
import {Mail, Phone, Trash2, Loader2, Inbox, Eye, CheckCircle, MessageSquare} from 'lucide-react'
import toast from 'react-hot-toast'
import type { ContactMessage } from '@/types'
import { useAuth, canEdit, canDelete } from '@/hooks/useAuth'

const TEAL = '#5BBDB5'
const GOLD = '#C8922A'
const NAVY = '#1E3A4A'

type Filter = 'all' | 'unread' | 'read' | 'replied'

export default function ManageContacts() {
  const { user } = useAuth()
  const allowEdit   = canEdit(user, 'mensagens')
  const allowDelete = canDelete(user, 'mensagens')
  const [messages, setMessages] = useState<ContactMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<Filter>('unread')
  const [actionId, setActionId] = useState<string | null>(null)
  const [selected, setSelected] = useState<ContactMessage | null>(null)

  const fetchMessages = useCallback(async () => {
    setLoading(true)
    try {
      const filterQuery = filter === 'all' ? {} : { status: filter }
      const { list } = await lumi.entities.contact_messages.list({
        filter: filterQuery,
        sort: { createdAt: -1 },
        limit: 100,
      })
      setMessages(list as ContactMessage[])
    } catch (e) {
      console.error('Erro ao carregar mensagens:', e)
      toast.error('Erro ao carregar mensagens de contato.')
    } finally {
      setLoading(false)
    }
  }, [filter])

  useEffect(() => { fetchMessages() }, [fetchMessages])

  const handleMarkRead = async (msg: ContactMessage) => {
    if (msg.status !== 'unread') return
    setActionId(msg._id)
    try {
      await lumi.entities.contact_messages.update(msg._id, {
        status: 'read',
        updatedAt: new Date().toISOString(),
      })
      await fetchMessages()
    } catch {
      toast.error('Erro ao atualizar status.')
    } finally {
      setActionId(null)
    }
  }

  const handleMarkReplied = async (id: string) => {
    setActionId(id)
    try {
      await lumi.entities.contact_messages.update(id, {
        status: 'replied',
        updatedAt: new Date().toISOString(),
      })
      toast.success('Marcado como respondido.')
      setSelected(prev => prev && prev._id === id ? { ...prev, status: 'replied' } : prev)
      await fetchMessages()
    } catch {
      toast.error('Erro ao atualizar status.')
    } finally {
      setActionId(null)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Excluir esta mensagem permanentemente?')) return
    setActionId(id)
    try {
      await lumi.entities.contact_messages.delete(id)
      toast.success('Mensagem excluída.')
      if (selected?._id === id) setSelected(null)
      await fetchMessages()
    } catch {
      toast.error('Erro ao excluir mensagem.')
    } finally {
      setActionId(null)
    }
  }

  const openDetail = async (msg: ContactMessage) => {
    setSelected(msg)
    if (msg.status === 'unread') {
      await handleMarkRead(msg)
    }
  }

  const counts = {
    all: messages.length,
    unread: messages.filter(m => m.status === 'unread').length,
    read: messages.filter(m => m.status === 'read').length,
    replied: messages.filter(m => m.status === 'replied').length,
  }

  const filterLabels: Record<Filter, string> = {
    all: 'Todas',
    unread: 'Não lidas',
    read: 'Lidas',
    replied: 'Respondidas',
  }

  const statusColors: Record<string, string> = {
    unread: GOLD,
    read: TEAL,
    replied: '#6b7280',
  }

  const statusLabels: Record<string, string> = {
    unread: 'Não lida',
    read: 'Lida',
    replied: 'Respondida',
  }

  const StatusIcon = ({ status }: { status: string }) => {
    if (status === 'unread') return <Mail size={11} />
    if (status === 'replied') return <CheckCircle size={11} />
    return <Eye size={11} />
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Cabeçalho */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Mensagens de Contato</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Mensagens enviadas pelo formulário de contato do site.
        </p>
      </div>

      {/* Cards de resumo */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total', value: (messages.length || 0).toString(), color: NAVY },
          { label: 'Não lidas', value: counts.unread.toString(), color: GOLD },
          { label: 'Lidas', value: counts.read.toString(), color: TEAL },
          { label: 'Respondidas', value: counts.replied.toString(), color: '#6b7280' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-4">
            <div className="text-2xl font-black" style={{ color }}>{value}</div>
            <div className="text-xs text-gray-400 font-semibold mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* Dica */}
      <div
        className="flex items-start gap-3 rounded-xl p-4 mb-6"
        style={{ background: '#DCF0EE', border: `1px solid ${TEAL}40` }}
      >
        <MessageSquare size={18} style={{ color: TEAL, flexShrink: 0, marginTop: 2 }} />
        <p className="text-sm" style={{ color: '#1e6b66' }}>
          Clique em uma mensagem para ver o conteúdo completo. Mensagens <strong>não lidas</strong> são marcadas como lidas automaticamente ao abrir. Marque como <strong>respondida</strong> após dar retorno ao visitante.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Lista */}
        <div className="flex-1 min-w-0">
          {/* Filtros */}
          <div className="flex flex-wrap gap-2 mb-4">
            {(Object.keys(filterLabels) as Filter[]).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className="px-4 py-2 rounded-lg text-sm font-semibold transition-all border"
                style={
                  filter === f
                    ? { background: TEAL, color: '#fff', borderColor: TEAL }
                    : { background: 'transparent', color: '#6b7280', borderColor: '#e5e7eb' }
                }
              >
                {filterLabels[f]}
                {f === 'unread' && counts.unread > 0 && (
                  <span
                    className="ml-2 px-1.5 py-0.5 rounded-full text-xs font-bold text-white"
                    style={{ background: GOLD }}
                  >
                    {counts.unread}
                  </span>
                )}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 size={28} className="animate-spin" style={{ color: TEAL }} />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-16">
              <Inbox size={40} className="mx-auto mb-3 text-gray-300" />
              <p className="text-gray-400 text-sm">Nenhuma mensagem {filter !== 'all' ? `"${filterLabels[filter].toLowerCase()}"` : ''} encontrada.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {messages.map(msg => (
                <div
                  key={msg._id}
                  onClick={() => openDetail(msg)}
                  className="bg-white dark:bg-gray-900 rounded-xl border p-4 cursor-pointer transition-all hover:shadow-md"
                  style={{
                    borderColor: selected?._id === msg._id ? TEAL : msg.status === 'unread' ? `${GOLD}60` : '#e5e7eb',
                    background: selected?._id === msg._id ? '#f0fbfa' : undefined,
                  }}
                >
                  <div className="flex items-start gap-3">
                    {/* Avatar */}
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                      style={{ background: msg.status === 'unread' ? GOLD : TEAL }}
                    >
                      {msg.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-sm font-bold text-gray-900 dark:text-white truncate ${msg.status === 'unread' ? 'font-black' : ''}`}>
                          {msg.name}
                        </span>
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold flex-shrink-0"
                          style={{
                            background: statusColors[msg.status] + '18',
                            color: statusColors[msg.status],
                          }}
                        >
                          <StatusIcon status={msg.status} />
                          {statusLabels[msg.status]}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 truncate">{msg.email}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">{msg.message}</p>
                      <p className="text-xs text-gray-300 mt-1">
                        {new Date(msg.createdAt).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    {allowDelete && (
                      <button
                        onClick={e => { e.stopPropagation(); handleDelete(msg._id) }}
                        disabled={actionId === msg._id}
                        className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all flex-shrink-0"
                        title="Excluir"
                      >
                        {actionId === msg._id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Painel de detalhe */}
        {selected && (
          <div className="lg:w-96 flex-shrink-0">
            <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-6 sticky top-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-bold text-gray-900 dark:text-white text-base">Detalhes da Mensagem</h3>
                <button
                  onClick={() => setSelected(null)}
                  className="text-xs text-gray-400 hover:text-gray-600 transition-colors px-2 py-1 rounded hover:bg-gray-100"
                >
                  Fechar
                </button>
              </div>

              {/* Avatar + Nome */}
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-white font-black text-lg flex-shrink-0"
                  style={{ background: TEAL }}
                >
                  {selected.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="font-bold text-gray-900 dark:text-white">{selected.name}</div>
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold"
                    style={{
                      background: statusColors[selected.status] + '18',
                      color: statusColors[selected.status],
                    }}
                  >
                    <StatusIcon status={selected.status} />
                    {statusLabels[selected.status]}
                  </span>
                </div>
              </div>

              {/* Contatos */}
              <div className="space-y-2 mb-5">
                <a
                  href={`mailto:${selected.email}`}
                  className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 hover:underline"
                >
                  <Mail size={15} style={{ color: TEAL }} />
                  {selected.email}
                </a>
                {selected.phone && (
                  <a
                    href={`tel:${selected.phone}`}
                    className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 hover:underline"
                  >
                    <Phone size={15} style={{ color: TEAL }} />
                    {selected.phone}
                  </a>
                )}
              </div>

              {/* Data */}
              <p className="text-xs text-gray-400 mb-4">
                Recebida em {new Date(selected.createdAt).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </p>

              {/* Mensagem */}
              <div
                className="rounded-xl p-4 mb-5 text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap"
                style={{ background: '#f8fffe', border: `1px solid ${TEAL}30` }}
              >
                {selected.message}
              </div>

              {/* Ações */}
              <div className="flex flex-col gap-2">
                <a
                  href={`mailto:${selected.email}?subject=Re: Mensagem recebida`}
                  className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold text-white transition-all"
                  style={{ background: TEAL }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#4aaba3')}
                  onMouseLeave={e => (e.currentTarget.style.background = TEAL)}
                >
                  <Mail size={15} />
                  Responder por E-mail
                </a>
                {selected.status !== 'replied' && allowEdit && (
                  <button
                    onClick={() => handleMarkReplied(selected._id)}
                    disabled={actionId === selected._id}
                    className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold border transition-all disabled:opacity-50"
                    style={{ borderColor: TEAL, color: TEAL }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#DCF0EE' }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
                  >
                    {actionId === selected._id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                    Marcar como respondida
                  </button>
                )}
                {allowDelete && (
                  <button
                    onClick={() => handleDelete(selected._id)}
                    disabled={actionId === selected._id}
                    className="flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-semibold text-red-400 hover:text-red-600 hover:bg-red-50 transition-all disabled:opacity-50"
                  >
                    <Trash2 size={13} />
                    Excluir mensagem
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
